import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import Footer from './Footer';
import './AdminUpload.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';

const AdminChaptersUpload = ({ user }) => {
  const [excelFile, setExcelFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chapters, setChapters] = useState([]);
  const [filterLang, setFilterLang] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [loadingChapters, setLoadingChapters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const languages = [
    { code: 'German', name: 'German' },
    { code: 'French', name: 'French' },
    { code: 'Spanish', name: 'Spanish' },
    { code: 'Italian', name: 'Italian' },
    { code: 'Japanese', name: 'Japanese' },
    { code: 'English', name: 'English' }
  ];

  const levels = [
    { code: 'A1', name: 'A1' },
    { code: 'A2', name: 'A2' },
    { code: 'B1', name: 'B1' },
    { code: 'B2', name: 'B2' },
    { code: 'C1', name: 'C1' },
    { code: 'C2', name: 'C2' }
  ];

  const handleUpload = async () => {
    if (!excelFile) { setError('Please select an Excel file'); return; }
    setUploading(true);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('excel', excelFile);

    try {
      const res = await axios.post(`${API_BASE}/chapters/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setResult(res.data);
      if (filterLang || filterLevel) {
        fetchChapters();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setUploading(false);
  };

  const fetchChapters = async () => {
    setLoadingChapters(true);
    try {
      const params = new URLSearchParams();
      if (filterLang) params.append('language', filterLang);
      if (filterLevel) params.append('proficiency_level', filterLevel);
      const res = await axios.get(`${API_BASE}/chapters/list?${params.toString()}`, { withCredentials: true });
      setChapters(res.data.chapters || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch chapters');
    }
    setLoadingChapters(false);
  };

  const handleDelete = async () => {
    if (!filterLang && !filterLevel) {
      setError('Please select at least a language or level to delete');
      return;
    }
    setDeleting(true);
    try {
      const res = await axios.post(`${API_BASE}/chapters/delete`, {
        language: filterLang || undefined,
        proficiency_level: filterLevel || undefined
      }, { withCredentials: true });
      setResult({ message: `Deleted ${res.data.deleted} chapters.`, deleted: res.data.deleted });
      setChapters([]);
      setDeleteConfirm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
    setDeleting(false);
  };

  useEffect(() => {
    if (filterLang || filterLevel) {
      fetchChapters();
    } else {
      setChapters([]);
    }
  }, [filterLang, filterLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="admin-upload-page">
      <Header user={user} />
      <main className="admin-upload-main">
        <h1>Chapters Upload (Learn Module)</h1>
        <p className="admin-upload-desc">Upload an Excel file to add or update chapters for the Learn module.</p>

        <div className="admin-upload-section">
          <h3>Excel File Format</h3>
          <p className="admin-upload-hint">
            Required columns: <strong>Language</strong> (German, French, etc.), <strong>Level</strong> (A1, A2, B1, etc.), 
            <strong> Chapter_Number</strong> (1, 2, 3...), <strong>Chapter_Name</strong> (e.g. Guten Tag)
          </p>
          <p className="admin-upload-hint">
            Optional columns: <strong>Topics</strong> (comma-separated, only needed on first row of each chapter), <strong>Prompts</strong> (one prompt per row)
          </p>
          <p className="admin-upload-hint" style={{ marginTop: '8px', color: '#555', fontStyle: 'italic' }}>
            Put each prompt on a separate row with the same Language/Level/Chapter_Number. The code groups them and builds the JSON automatically.
          </p>
          <div style={{ marginTop: '12px' }}>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={e => { setExcelFile(e.target.files[0]); setResult(null); setError(''); }} />
          </div>
        </div>

        <button className="admin-upload-btn" onClick={handleUpload} disabled={uploading || !excelFile}>
          {uploading ? 'Uploading...' : 'Upload Chapters'}
        </button>

        {error && <div className="admin-upload-error">{error}</div>}
        {result && (
          <div className="admin-upload-result">
            <h3>✅ {result.message || 'Upload Complete'}</h3>
            {result.inserted !== undefined && <p>Inserted: <strong>{result.inserted}</strong></p>}
            {result.updated !== undefined && <p>Updated: <strong>{result.updated}</strong></p>}
            {result.skipped !== undefined && result.skipped > 0 && <p>Skipped: <strong>{result.skipped}</strong></p>}
            {result.deleted !== undefined && <p>Deleted: <strong>{result.deleted}</strong></p>}
            {result.errors && result.errors.length > 0 && (
              <div className="admin-upload-errors">
                <h4>Errors:</h4>
                {result.errors.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
          </div>
        )}

        <hr style={{ margin: '32px 0', borderColor: '#E0E0E0' }} />

        <h2 style={{ fontFamily: 'Graphik-Semibold', fontSize: '20px', color: '#333', marginBottom: '16px' }}>View / Delete Existing Chapters</h2>
        
        <div className="admin-upload-section" style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={filterLang} onChange={e => setFilterLang(e.target.value)} style={{ padding: '8px 12px', fontSize: '14px', border: '1px solid #CFCFCF' }}>
            <option value="">All Languages</option>
            {languages.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} style={{ padding: '8px 12px', fontSize: '14px', border: '1px solid #CFCFCF' }}>
            <option value="">All Levels</option>
            {levels.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          <button onClick={fetchChapters} style={{ padding: '8px 16px', background: '#7500C0', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'Graphik-Semibold', fontSize: '14px' }}>
            {loadingChapters ? 'Loading...' : 'Refresh'}
          </button>
          {(filterLang || filterLevel) && chapters.length > 0 && (
            <>
              {!deleteConfirm ? (
                <button onClick={() => setDeleteConfirm(true)} style={{ padding: '8px 16px', background: '#DC2626', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'Graphik-Semibold', fontSize: '14px' }}>
                  Delete Filtered
                </button>
              ) : (
                <span style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ color: '#DC2626', fontSize: '13px' }}>Delete {chapters.length} chapters?</span>
                  <button onClick={handleDelete} disabled={deleting} style={{ padding: '6px 12px', background: '#DC2626', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
                    {deleting ? 'Deleting...' : 'Confirm'}
                  </button>
                  <button onClick={() => setDeleteConfirm(false)} style={{ padding: '6px 12px', background: '#888', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
                    Cancel
                  </button>
                </span>
              )}
            </>
          )}
        </div>

        {chapters.length > 0 && (
          <div style={{ marginTop: '16px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Graphik', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#F1F1EF', textAlign: 'left' }}>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>ID</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Language</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Level</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Chapter</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Name</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Topics</th>
                  <th style={{ padding: '10px 8px', borderBottom: '1px solid #CFCFCF' }}>Prompts</th>
                </tr>
              </thead>
              <tbody>
                {chapters.map(ch => (
                  <tr key={ch.id} style={{ borderBottom: '1px solid #E0E0E0' }}>
                    <td style={{ padding: '8px' }}>{ch.id}</td>
                    <td style={{ padding: '8px' }}>{ch.language}</td>
                    <td style={{ padding: '8px' }}>{ch.proficiency_level}</td>
                    <td style={{ padding: '8px' }}>{ch.chapter_title}</td>
                    <td style={{ padding: '8px', fontWeight: '500' }}>{ch.chapter_description}</td>
                    <td style={{ padding: '8px', maxWidth: '200px', fontSize: '12px' }}>
                      {Array.isArray(ch.chapter_topics) ? ch.chapter_topics.map(t => t.title).join(', ') : ''}
                    </td>
                    <td style={{ padding: '8px', fontSize: '12px' }}>
                      {Array.isArray(ch.chapter_prompts) ? ch.chapter_prompts.length + ' prompts' : '0'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ marginTop: '8px', fontSize: '13px', color: '#808080' }}>Showing {chapters.length} chapters</p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default AdminChaptersUpload;
